import { Brain, Dumbbell, Salad, ScanLine, Smile, Stethoscope } from 'lucide-react';

// Base común de Medix que sirve para cualquier especialidad (catálogo "transversal").
export const BASE_MODULES = [
  { name: 'Agenda por profesional y box', text: 'Horarios configurables, disponibilidad real y citas en pocos clics.' },
  { name: 'Fichas clínicas personalizadas', text: 'Crea tus propias plantillas de ficha y digitaliza formularios en papel.' },
  { name: 'Consentimiento informado', text: 'Plantilla editable, firmada en pantalla y guardada en la ficha.' },
  { name: 'Recetas y órdenes', text: 'Medicamentos y exámenes desde el catálogo de tu consulta.' },
  { name: 'Informe de derivación', text: 'Carta al especialista con los antecedentes del paciente ya cargados.' },
  { name: 'Equipo, roles y auditoría', text: 'Permisos por rol y registro de quién hizo qué y cuándo.' },
];

// Contenido por especialidad. `modules` = módulos propios disponibles (beta) según el catálogo del sistema;
// `soon` = módulos propios marcados como "próximamente" en el catálogo. No prometer más que eso.
export const SPECIALTY_PAGES = [
  {
    slug: 'odontologia',
    name: 'Odontología',
    icon: Smile,
    title: 'Software para clínicas dentales y odontólogos',
    lead: 'Odontograma por pieza, radiografías junto al paciente, agenda por sillón y consentimientos firmados en pantalla. Todo en un solo sistema, hecho en Chile.',
    mock: 'odontograma',
    pains: [
      'Odontogramas en papel que no muestran la evolución del paciente.',
      'Radiografías guardadas en otro computador o en el correo.',
      'Consentimientos impresos para cada tratamiento.',
    ],
    modules: [
      { name: 'Odontograma', text: 'Nomenclatura FDI, hallazgos por pieza y superficie, y plan por pieza.' },
      { name: 'Estudios RX', text: 'Panorámicas, periapicales, CBCT y PDF guardados en la ficha del paciente.' },
      { name: 'Informe radiológico', text: 'Plantillas, borrador, emisión e impresión del informe por estudio.' },
      { name: 'Comparador de series', text: 'Compara radiografías antes y después, con zoom sincronizado.' },
    ],
    soon: ['Periodontograma', 'Plan de tratamiento dental con presupuesto', 'Consentimiento dental específico', 'Endodoncia', 'Ortodoncia'],
    faq: [
      { q: '¿El odontograma usa nomenclatura FDI?', a: 'Sí. Registra hallazgos por pieza y superficie, en dentición permanente y temporal.' },
      { q: '¿Puedo ver las radiografías dentro de la ficha?', a: 'Sí. Los estudios RX quedan asociados al paciente, y puedes compararlos entre controles y emitir el informe.' },
      { q: '¿Sirve para una consulta de un solo odontólogo?', a: 'Sí. El plan Solo está pensado para profesionales independientes.' },
    ],
  },
  {
    slug: 'imagenologia',
    name: 'Imagenología',
    icon: ScanLine,
    title: 'Gestión de estudios radiológicos e informes',
    lead: 'Sube los estudios, compáralos entre controles y emite informes radiológicos con plantillas. Con agenda y fichas del paciente en el mismo sistema.',
    mock: 'rx',
    pains: [
      'Estudios repartidos entre carpetas, correos y pendrives.',
      'Informes redactados desde cero en un procesador de texto.',
      'Difícil comparar un control con el anterior.',
    ],
    modules: [
      { name: 'Estudios RX', text: 'Panorámicas, periapicales, CBCT y PDF organizados por paciente.' },
      { name: 'Informe radiológico', text: 'Plantillas, borrador, emisión e impresión del informe formal.' },
      { name: 'Comparador de series', text: 'Antes y después en la misma vista, con zoom sincronizado.' },
    ],
    soon: [],
    faq: [
      { q: '¿Qué tipos de estudio puedo subir?', a: 'Panorámicas, periapicales, CBCT y documentos PDF, asociados a cada paciente.' },
      { q: '¿Puedo usar mis propias plantillas de informe?', a: 'Sí. El informe radiológico trabaja con plantillas, borrador y emisión.' },
    ],
  },
  {
    slug: 'medicina-general',
    name: 'Medicina general',
    icon: Stethoscope,
    title: 'Software para consultas de medicina general',
    lead: 'Agenda, ficha clínica con tus propias plantillas, recetas, órdenes de exámenes y derivaciones en un solo lugar.',
    mock: 'ficha',
    pains: [
      'Recetas y órdenes escritas a mano en cada consulta.',
      'Historial del paciente incompleto o repartido.',
      'Cartas de derivación redactadas desde cero.',
    ],
    modules: [],
    soon: ['Anamnesis general estructurada', 'Control crónico (HTA / DM)', 'Certificados y licencias', 'Examen preocupacional', 'Teleconsulta'],
    faq: [
      { q: '¿Puedo usar Medix hoy en medicina general?', a: 'Sí. La base de Medix (agenda, fichas personalizadas, recetas, órdenes, consentimientos y derivaciones) funciona hoy. Los módulos específicos de medicina general están en desarrollo.' },
      { q: '¿Puedo crear mi propia ficha?', a: 'Sí. Puedes armar plantillas de ficha a tu medida y digitalizar formularios en papel.' },
    ],
  },
  {
    slug: 'psicologia',
    name: 'Psicología',
    icon: Brain,
    title: 'Software para psicólogos y centros de salud mental',
    lead: 'Agenda de sesiones, fichas a tu medida, consentimientos firmados y control de acceso para proteger la información de tus pacientes.',
    mock: 'agenda',
    pains: [
      'Notas de sesión en cuadernos o documentos sueltos.',
      'Agenda llevada por WhatsApp.',
      'Información sensible sin control de quién la ve.',
    ],
    modules: [],
    soon: ['Intake psicológico', 'Sesión de seguimiento', 'Evaluación infantil / escolar', 'Terapia de pareja y familia'],
    faq: [
      { q: '¿Quién puede ver las fichas de mis pacientes?', a: 'Solo las personas de tu equipo con el rol y los permisos que tú definas. Además, queda registro de quién hizo cada cambio.' },
      { q: '¿Puedo usar Medix hoy en psicología?', a: 'Sí. La agenda, las fichas personalizadas y los consentimientos funcionan hoy. Los módulos específicos de psicología están en desarrollo.' },
    ],
  },
  {
    slug: 'kinesiologia',
    name: 'Kinesiología',
    icon: Dumbbell,
    title: 'Software para kinesiólogos y centros de rehabilitación',
    lead: 'Agenda por box, fichas de evaluación a tu medida y seguimiento de cada paciente en un solo sistema.',
    mock: 'agenda',
    pains: [
      'Sesiones y boxes coordinados en una planilla.',
      'Evaluaciones en papel difíciles de comparar.',
      'Cada kinesiólogo con su propio registro.',
    ],
    modules: [],
    soon: ['Evaluación funcional', 'Plan de rehabilitación', 'Kinesiología respiratoria', 'Dolor lumbar / cervical con escalas'],
    faq: [
      { q: '¿Puedo organizar la agenda por box?', a: 'Sí. La agenda se ve por profesional y por box, con horarios configurables.' },
      { q: '¿Puedo usar Medix hoy en kinesiología?', a: 'Sí. La base de Medix funciona hoy con fichas a tu medida. Los módulos específicos de kinesiología están en desarrollo.' },
    ],
  },
  {
    slug: 'nutricion',
    name: 'Nutrición',
    icon: Salad,
    title: 'Software para nutricionistas',
    lead: 'Agenda de controles, fichas de evaluación a tu medida y el historial de cada paciente, siempre ordenado.',
    mock: 'ficha',
    pains: [
      'Controles anotados en planillas separadas por paciente.',
      'Difícil ver la evolución entre controles.',
      'Agenda y recordatorios llevados a mano.',
    ],
    modules: [],
    soon: ['Evaluación nutricional (antropometría y recordatorio 24 h)', 'Plan alimentario', 'Diabetes / metabólico', 'Nutrición deportiva'],
    faq: [
      { q: '¿Puedo usar Medix hoy en nutrición?', a: 'Sí. La agenda, las fichas personalizadas y el historial funcionan hoy. Los módulos específicos de nutrición están en desarrollo.' },
    ],
  },
];

export const findSpecialty = (slug) => SPECIALTY_PAGES.find((s) => s.slug === slug);
